import React, { useState, useCallback, useRef, useEffect } from 'react';
import { FiZap, FiDownload, FiCheck, FiRefreshCw, FiMaximize2, FiCamera, FiX, FiImage, FiEye } from 'react-icons/fi';
import api from '../api';

const STYLES = [
  { id: 'Elegant Floral', label: 'Elegant Floral', icon: '\u{1F338}', category: 'classic' },
  { id: 'Modern Minimal', label: 'Modern Minimal', icon: '\u2728', category: 'classic' },
  { id: 'Playful Party', label: 'Playful Party', icon: '\u{1F389}', category: 'classic' },
  { id: 'Rustic Charm', label: 'Rustic Charm', icon: '\u{1FAB5}', category: 'classic' },
  { id: 'Golden Glamour', label: 'Golden Glamour', icon: '\u2728', category: 'classic' },
  { id: 'Bohemian', label: 'Bohemian', icon: '\u{1F33F}', category: 'classic' },
  { id: 'Classic White', label: 'Classic White', icon: '\u{1F90D}', category: 'classic' },
  { id: 'Romantic', label: 'Romantic', icon: '\u{1F495}', category: 'classic' },
  { id: 'Bold & Bright', label: 'Bold & Bright', icon: '\u{1F3A8}', category: 'classic' },
  { id: 'Vintage', label: 'Vintage', icon: '\u{1F4DC}', category: 'classic' },
  { id: '3D Realistic Card', label: '3D Realistic Card', icon: '\u{1F4F8}', category: '3d' },
  { id: '3D Scene Setting', label: '3D Scene Setting', icon: '\u{1F3DE}\uFE0F', category: '3d' },
  { id: 'Plain Elegant', label: 'Plain Elegant', icon: '\u{1F48E}', category: 'elegant' },
  { id: 'Luxury', label: 'Luxury', icon: '\u{1F451}', category: 'elegant' },
  { id: 'African Heritage', label: 'African Heritage', icon: '\u{1F30D}', category: 'sa' },
  { id: 'Shweshwe Chic', label: 'Shweshwe Chic', icon: '\u{1F537}', category: 'sa' },
  { id: 'Zulu Royal', label: 'Zulu Royal', icon: '\u{1F6E1}\uFE0F', category: 'sa' },
  { id: 'Ndebele Art', label: 'Ndebele Art', icon: '\u{1F536}', category: 'sa' },
];

const SIZE_OPTIONS = [
  { label: 'Portrait', value: 'portrait', desc: 'Standard invite', icon: '📱' },
  { label: 'Square', value: 'square', desc: 'Social media', icon: '⬜' },
  { label: 'Landscape', value: 'landscape', desc: 'Banner / email', icon: '🖼️' },
  { label: 'Story', value: 'story', desc: 'WhatsApp status', icon: '📲' },
];

const THEME_SUGGESTIONS = {
  'Kids Party': ['Barbie', 'Frozen', 'Dinosaurs', 'Unicorn', 'PAW Patrol', 'Princess', 'Superhero', 'Under the Sea', 'Safari', 'Rainbow', 'Minecraft', 'Pink Glitter'],
  'Birthday': ['Gold & Black', 'Rose Gold', 'Vintage', 'Tropical', 'Neon Glow', 'Garden Party', 'Gatsby', 'Boho Chic', 'Afro-chic', 'Pastel Dream'],
  'Wedding': ['Floral Romance', 'Rustic Charm', 'Modern Minimalist', 'African Elegance', 'Gold & White', 'Bohemian', 'Royal Blue', 'Blush & Cream', 'Protea & Fynbos', 'Ndebele Patterns', 'Zulu Beadwork'],
  'Traditional Wedding': ['Zulu Royal', 'Xhosa Heritage', 'Sotho Blankets', 'Ndebele Art', 'Shweshwe Fabric', 'African Earth & Gold', 'Venda Tradition', 'Tsonga Heritage', 'Ubuntu Spirit'],
  'Funeral': ['Peaceful', 'Dignified White', 'Gentle Remembrance', 'Classic Memorial', 'Serene Blue', 'In Loving Memory', 'Eternal Rest'],
  'Memorial / Unveiling': ['Peaceful', 'Dignified White', 'Gentle Remembrance', 'Tombstone Unveiling', 'Eternal Light'],
  'Lobola': ['Traditional Earth', 'African Heritage', 'Royal African', 'Warm Gold', 'Ubuntu Spirit', 'Zulu Shields & Spears', 'Xhosa Blankets', 'Sotho Heritage', 'Ndebele Geometric', 'Cattle & Kraal'],
  'Umembeso': ['Zulu Heritage', 'African Print', 'Royal Purple', 'Earth & Gold', 'Zulu Beadwork', 'Isicholo & Isidwaba', 'Traditional Gifts'],
  'Umemulo': ['Zulu Coming of Age', 'Royal Maiden', 'Isicholo Crown', 'Red Ochre & Beads', 'Zulu Pride', 'African Princess', 'Traditional Elegance'],
  'Engagement': ['Rose Gold', 'Diamond Sparkle', 'Classic Romantic', 'Champagne Toast', 'Blush & Gold', 'Garden Romance'],
  'Baby Shower': ['Pastel Dream', 'Teddy Bear', 'Stork & Clouds', 'Pink Princess', 'Blue Prince', 'Gender Neutral Green', 'Safari Animals'],
  'Gender Reveal': ['Pink or Blue', 'Pastel Balloons', 'Twinkle Star', 'He or She', 'Team Pink Team Blue'],
  'Graduation': ['Academic Gold', 'Achievement Blue', 'Modern Success', 'Confetti Celebration', 'Cap & Gown'],
  'Matric Dance': ['Hollywood Glamour', 'Starry Night', 'Masquerade Ball', 'Neon Lights', 'Black Tie'],
  'Corporate Event': ['Professional Blue', 'Executive Black', 'Modern Corporate', 'Tech Minimal', 'Gala Night'],
  'Stokvel': ['Community Spirit', 'Gold & Green', 'Money Theme', 'African Unity', 'Ubuntu Savings'],
  'Mokete': ['Sotho Heritage', 'Traditional Feast', 'African Celebration', 'Cultural Pride'],
};

// Intelligent color palette matching for invitation themes (#15)
const INVITATION_PALETTES = {
  // Floral themes → soft pinks, greens, lavenders
  'Elegant Floral': { primary: '#D4A5B9', secondary: '#8FBF9F', accent: '#B8A9C9', text: '#3D2C3A', bg: '#FDF6F9', description: 'Soft rose pink with sage green and lavender accents — romantic garden feel' },
  // Modern minimal → clean neutrals, monochrome
  'Modern Minimal': { primary: '#2D2D2D', secondary: '#F5F5F5', accent: '#C9A96E', text: '#1A1A1A', bg: '#FFFFFF', description: 'Clean black and white with warm gold accent — sophisticated simplicity' },
  // Playful party → bright primaries
  'Playful Party': { primary: '#FF6B6B', secondary: '#4ECDC4', accent: '#FFE66D', text: '#2C3E50', bg: '#FFF9F0', description: 'Vibrant coral with teal and sunny yellow — festive and fun' },
  // Rustic charm → earth tones, warm browns
  'Rustic Charm': { primary: '#8B6F47', secondary: '#D4A574', accent: '#6B8E4E', text: '#3E2C1C', bg: '#FAF3E8', description: 'Warm wood tones with sage green and cream — natural countryside warmth' },
  // Golden glamour → gold, black, champagne
  'Golden Glamour': { primary: '#C9A96E', secondary: '#1A1A1A', accent: '#F5E6CC', text: '#2D2D2D', bg: '#FDFBF7', description: 'Luxurious gold on black with champagne highlights — red carpet elegance' },
  // Bohemian → terracotta, turquoise, warm
  'Bohemian': { primary: '#C2654A', secondary: '#5BA4B2', accent: '#E8C46A', text: '#4A3728', bg: '#FEF9F0', description: 'Earthy terracotta with turquoise and mustard — free-spirited artistry' },
  // Classic white → white, cream, subtle color
  'Classic White': { primary: '#FFFFFF', secondary: '#F0EDE8', accent: '#A8C4A2', text: '#333333', bg: '#FEFEFE', description: 'Pure white with cream and subtle sage — timeless and refined' },
  // Romantic → blush, deep reds, rose gold
  'Romantic': { primary: '#E8B4B8', secondary: '#9B2D3A', accent: '#D4A89C', text: '#4A2025', bg: '#FEF5F6', description: 'Blush pink with deep crimson and rose gold — passionate and tender' },
  // Bold & bright → vivid colors, high contrast
  'Bold & Bright': { primary: '#E3425C', secondary: '#2ECC71', accent: '#3498DB', text: '#1A1A2E', bg: '#FFFFFF', description: 'Vivid rose with emerald and cobalt blue — energetic and daring' },
  // Vintage → sepia, muted, nostalgic
  'Vintage': { primary: '#C4A882', secondary: '#8B7355', accent: '#D4C5B9', text: '#4A3728', bg: '#FDF8F2', description: 'Sepia browns with muted cream and antique gold — nostalgic charm' },
  // 3D card styles
  '3D Realistic Card': { primary: '#1a1a2e', secondary: '#e8d5b7', accent: '#c9a96e', text: '#1a1a2e', bg: '#fdfbf7', description: 'Deep navy with cream and gold — photorealistic printed invitation on premium cardstock' },
  '3D Scene Setting': { primary: '#2d3436', secondary: '#dfe6e9', accent: '#b2bec3', text: '#2d3436', bg: '#f5f6fa', description: 'Slate gray with soft silver — atmospheric 3D scene with event decor backdrop' },
  // Elegant styles
  'Plain Elegant': { primary: '#f5f0eb', secondary: '#2c2c2c', accent: '#8b7355', text: '#2c2c2c', bg: '#fdfbf8', description: 'Warm cream with charcoal and taupe — understated refined elegance' },
  'Luxury': { primary: '#0a0a0a', secondary: '#d4af37', accent: '#8b0000', text: '#f5f5f5', bg: '#0a0a0a', description: 'Black with rich gold and deep crimson — exclusive high-end luxury' },
  // SA Traditional styles
  'African Heritage': { primary: '#8b4513', secondary: '#daa520', accent: '#2f1b0e', text: '#2f1b0e', bg: '#fdf5e6', description: 'Earth browns with goldenrod — traditional African warmth, kente textures, baobab silhouettes' },
  'Shweshwe Chic': { primary: '#1a3a5c', secondary: '#ffffff', accent: '#c4a35a', text: '#0d2137', bg: '#f8fafc', description: 'Indigo blue with white geometric dots — Shweshwe fabric texture, traditional SA dress' },
  'Zulu Royal': { primary: '#b8860b', secondary: '#8b0000', accent: '#000000', text: '#1a1a1a', bg: '#fefdf7', description: 'Dark gold with royal red and black — Zulu shields, spears, leopard print, isicholo crown' },
  'Ndebele Art': { primary: '#ff6b35', secondary: '#ffd700', accent: '#1a1a2e', text: '#1a1a2e', bg: '#fffef9', description: 'Vibrant orange with yellow and ink blue — Ndebele geometric wall art patterns, bold symmetry' },
};

// Event type to recommended style palette mapping
const EVENT_PALETTE_RECOMMENDATIONS = {
  'Wedding': ['Elegant Floral', 'Classic White', 'Romantic', 'Golden Glamour', 'Luxury', '3D Realistic Card'],
  'Lobola': ['Zulu Royal', 'African Heritage', 'Rustic Charm', 'Golden Glamour', 'Ndebele Art'],
  'Umembeso': ['Zulu Royal', 'Shweshwe Chic', 'African Heritage', 'Golden Glamour', 'Ndebele Art'],
  'Umemulo': ['Zulu Royal', 'Bold & Bright', 'African Heritage', 'Romantic'],
  'Birthday': ['Playful Party', 'Bold & Bright', 'Modern Minimal', 'Golden Glamour', '3D Realistic Card'],
  'Kids Party': ['Playful Party', 'Bold & Bright', 'Bohemian'],
  'Graduation': ['Modern Minimal', 'Golden Glamour', 'Classic White', 'Bold & Bright'],
  'Gender Reveal': ['Playful Party', 'Modern Minimal', 'Classic White'],
  'Engagement': ['Romantic', 'Elegant Floral', 'Classic White', 'Golden Glamour', 'Luxury'],
  'Memorial / Unveiling': ['Classic White', 'Plain Elegant', 'Modern Minimal', 'Vintage'],
  'Funeral': ['Classic White', 'Plain Elegant', 'Modern Minimal', 'Vintage'],
  'Baby Shower': ['Elegant Floral', 'Playful Party', 'Bohemian', 'Classic White'],
  'Matric Dance': ['Golden Glamour', 'Luxury', 'Bold & Bright', 'Modern Minimal', 'Romantic'],
  'Corporate Event': ['Modern Minimal', 'Plain Elegant', 'Classic White', 'Golden Glamour'],
  'Stokvel': ['African Heritage', 'Bold & Bright', 'Golden Glamour', 'Modern Minimal'],
  'Mokete': ['African Heritage', 'Shweshwe Chic', 'Bohemian', 'Rustic Charm'],
};

const getRecommendedStylesForEvent = (eventType) => {
  return EVENT_PALETTE_RECOMMENDATIONS[eventType] || ['Modern Minimal', 'Elegant Floral', 'Classic White'];
};

const trackAction = (action, data) => {
  api.post('/ai-design/track', { action, ...data }).catch(() => {});
};

export default function AIInvitationDesigner({ eventId, eventType: eventTypeProp, onUseDesign, onClose }) {
  const eventType = eventTypeProp || '';
  const recommendedStyles = getRecommendedStylesForEvent(eventType);
  const [themeKeyword, setThemeKeyword] = useState('');
  const [selectedStyles, setSelectedStyles] = useState(recommendedStyles.length ? [recommendedStyles[0]] : ['Elegant Floral']);
  const [useColorSchemeBg, setUseColorSchemeBg] = useState(false);
  const toggleStyle = (styleId) => {
    setSelectedStyles(prev => {
      if (prev.includes(styleId)) {
        if (prev.length <= 1) return prev;
        return prev.filter(s => s !== styleId);
      }
      return [...prev, styleId];
    });
  };
  const [loading, setLoading] = useState(false);
  const [imageUrl, setImageUrl] = useState(null);
  const [textResponse, setTextResponse] = useState('');
  const [error, setError] = useState(null);
  const [history, setHistory] = useState([]);
  const [historyIndex, setHistoryIndex] = useState(-1);
  const [fullscreen, setFullscreen] = useState(false);
  const [uploadedPhoto, setUploadedPhoto] = useState(null);
  const [photoPreview, setPhotoPreview] = useState(null);
  const [inspirationImage, setInspirationImage] = useState(null);
  const [inspirationPreview, setInspirationPreview] = useState(null);
  const [imageSize, setImageSize] = useState('portrait');
  const [premiumLoading, setPremiumLoading] = useState(false);
  const [premiumData, setPremiumData] = useState(null); // { clean_image_url, video_url }
  const [previewVideoUrl, setPreviewVideoUrl] = useState(null);
  const [generatingPreview, setGeneratingPreview] = useState(false);
  const [previewProgramme, setPreviewProgramme] = useState(null);
  const [previewWelcomeBoard, setPreviewWelcomeBoard] = useState(null);
  const [generatingProgramme, setGeneratingProgramme] = useState(false);
  const [generatingWelcomeBoard, setGeneratingWelcomeBoard] = useState(false);
  const [activePreview, setActivePreview] = useState(null); // 'invitation' | 'video' | 'programme' | 'welcome'
  const [designPreviewUrl, setDesignPreviewUrl] = useState(null);
  const [generatingDesignPreview, setGeneratingDesignPreview] = useState(false);
  const [savedDesigns, setSavedDesigns] = useState([]);
  const [showSaved, setShowSaved] = useState(false);
  const [headingOption, setHeadingOption] = useState('');
  const fileInputRef = useRef(null);
  const inspoInputRef = useRef(null);

  // Load saved designs on mount
  useEffect(() => {
    api.get(`/events/${eventId}/ai-designs`).then(res => {
      if (res.data && res.data.length > 0) {
        setSavedDesigns(res.data);
        // Also populate history so Previous/Next works
        const historyItems = res.data.map(d => ({ image_url: d.image_url, theme: d.theme_keyword, style: d.style }));
        setHistory(historyItems);
        setHistoryIndex(historyItems.length - 1);
        setImageUrl(historyItems[historyItems.length - 1].image_url);
      }
    }).catch(() => {});
  }, [eventId]);

  const suggestions = (THEME_SUGGESTIONS[eventType] || THEME_SUGGESTIONS['Birthday'] || []).filter(Boolean);
  const analyticsData = () => ({ event_id: eventId, theme_keyword: themeKeyword, style: selectedStyles.join(' + '), event_type: eventType, image_url: imageUrl });

  const handlePhotoUpload = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setUploadedPhoto(file);
    const reader = new FileReader();
    reader.onload = (ev) => setPhotoPreview(ev.target.result);
    reader.readAsDataURL(file);
  };

  const removePhoto = () => {
    setUploadedPhoto(null);
    setPhotoPreview(null);
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const handleInspirationUpload = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setInspirationImage(file);
    const reader = new FileReader();
    reader.onload = (ev) => setInspirationPreview(ev.target.result);
    reader.readAsDataURL(file);
  };

  const removeInspiration = () => {
    setInspirationImage(null);
    setInspirationPreview(null);
    if (inspoInputRef.current) inspoInputRef.current.value = '';
  };

  const generateDesign = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const hasFiles = uploadedPhoto || inspirationImage;
      let r;
      if (hasFiles) {
        const fd = new FormData();
        fd.append('theme_keyword', themeKeyword);
        fd.append('styles', JSON.stringify(selectedStyles));
        fd.append('image_size', imageSize);
        fd.append('useColorSchemeBg', useColorSchemeBg);
        if (headingOption && headingOption !== 'custom') fd.append('invitation_heading', headingOption);
        if (uploadedPhoto) fd.append('photo', uploadedPhoto);
        if (inspirationImage) fd.append('inspiration', inspirationImage);
        r = await api.post(`/events/${eventId}/ai-invitation-design`, fd, {
          headers: { 'Content-Type': 'multipart/form-data' },
          timeout: 60000,
        });
      } else {
        r = await api.post(`/events/${eventId}/ai-invitation-design`, {
          theme_keyword: themeKeyword,
          styles: selectedStyles,
          image_size: imageSize,
          useColorSchemeBg: useColorSchemeBg,
          invitation_heading: (headingOption && headingOption !== 'custom') ? headingOption : undefined,
        }, { timeout: 60000 });
      }
      const data = r.data;
      if (data.image_url) {
        setImageUrl(data.image_url);
        setDesignPreviewUrl(null);
        setTextResponse(data.text_response || '');
        const entry = { image_url: data.image_url, text_response: data.text_response || '' };
        setHistory(prev => [...prev, entry]);
        setHistoryIndex(history.length);
      } else if (data.fallback) {
        setError(data.error_hint || 'AI image generation is temporarily unavailable. Try a different theme or try again.');
        setImageUrl(null);
      } else {
        setError('No image was generated. Please try again.');
      }
    } catch (err) {
      if (err.response?.status === 429) {
        const data = err.response.data;
        setError(`Daily limit reached (${data.used || 20}/${data.limit || 20} designs used). Your limit resets in 24 hours.`);
      } else {
        setError('Failed to generate design. Please try again.');
      }
    }
    setLoading(false);
  }, [eventId, themeKeyword, selectedStyles, useColorSchemeBg, history.length, uploadedPhoto, inspirationImage, headingOption]);

  const generateDesignPreview = useCallback(async () => {
    setGeneratingDesignPreview(true);
    setError(null);
    setDesignPreviewUrl(null);
    try {
      const hasFiles = uploadedPhoto || inspirationImage;
      let r;
      if (hasFiles) {
        const fd = new FormData();
        fd.append('theme_keyword', themeKeyword);
        fd.append('styles', JSON.stringify(selectedStyles));
        fd.append('image_size', imageSize);
        fd.append('preview', 'true');
        fd.append('useColorSchemeBg', useColorSchemeBg);
        if (headingOption && headingOption !== 'custom') fd.append('invitation_heading', headingOption);
        if (uploadedPhoto) fd.append('photo', uploadedPhoto);
        if (inspirationImage) fd.append('inspiration', inspirationImage);
        r = await api.post(`/events/${eventId}/ai-invitation-design`, fd, {
          headers: { 'Content-Type': 'multipart/form-data' },
          timeout: 30000,
        });
      } else {
        r = await api.post(`/events/${eventId}/ai-invitation-design`, {
          theme_keyword: themeKeyword,
          styles: selectedStyles,
          image_size: imageSize,
          preview: true,
          useColorSchemeBg: useColorSchemeBg,
          invitation_heading: (headingOption && headingOption !== 'custom') ? headingOption : undefined,
        }, { timeout: 30000 });
      }
      if (r.data.preview_url) {
        setDesignPreviewUrl(r.data.preview_url);
      } else {
        setError('Preview generation failed. Please try again.');
      }
    } catch (err) {
      setError('Failed to generate preview. Please try again.');
    }
    setGeneratingDesignPreview(false);
  }, [eventId, themeKeyword, selectedStyles, useColorSchemeBg, uploadedPhoto, inspirationImage, headingOption]);

  const goPrevious = () => {
    if (historyIndex > 0) {
      const idx = historyIndex - 1;
      setHistoryIndex(idx);
      setImageUrl(history[idx].image_url);
    }
  };

  const goNext = () => {
    if (historyIndex < history.length - 1) {
      const idx = historyIndex + 1;
      setHistoryIndex(idx);
      setImageUrl(history[idx].image_url);
    }
  };

  const downloadImage = async () => {
    if (!imageUrl) return;
    trackAction('download', analyticsData());
    try {
      const response = await fetch(imageUrl);
      const blob = await response.blob();
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.download = `invitation-grandgather-${Date.now()}.png`;
      link.href = url;
      link.click();
      URL.revokeObjectURL(url);
    } catch { alert('Failed to download image'); }
  };

  const applyAsInvitation = async () => {
    if (!imageUrl) return;
    trackAction('use_as_invitation', analyticsData());
    try {
      const response = await fetch(imageUrl);
      const blob = await response.blob();
      const file = new File([blob], 'ai-invitation.png', { type: blob.type || 'image/png' });
      onUseDesign(file);
    } catch { alert('Failed to use image as invitation'); }
  };

  const openFullscreen = () => {
    trackAction('fullscreen', analyticsData());
    setFullscreen(true);
  };

  // Generate watermarked preview video
  const generatePreview = async () => {
    if (!imageUrl || generatingPreview) return;
    setGeneratingPreview(true);
    setActivePreview('video');
    try {
      const res = await api.post('/ai-design/preview-video', { image_url: imageUrl, event_id: eventId }, { timeout: 300000 });
      if (res.data.preview_url) setPreviewVideoUrl(res.data.preview_url + '?t=' + Date.now());
    } catch (err) { console.error('Preview video error:', err); alert('Video generation takes up to 2 minutes with AI animation. Please try again.'); }
    setGeneratingPreview(false);
  };

  // Generate programme card preview
  const generateProgrammePreview = async () => {
    if (generatingProgramme) return;
    setGeneratingProgramme(true);
    setActivePreview('programme');
    try {
      const res = await api.post(`/events/${eventId}/ai-generate-extra`, { type: 'programme' });
      if (res.data.image_url) setPreviewProgramme(res.data.image_url);
    } catch (err) {
      const msg = err.response?.data?.error || 'Programme preview failed. Make sure you have timeline items added.';
      alert(msg);
    }
    setGeneratingProgramme(false);
  };

  // Generate welcome board preview
  const generateWelcomeBoardPreview = async () => {
    if (generatingWelcomeBoard) return;
    setGeneratingWelcomeBoard(true);
    setActivePreview('welcome');
    try {
      const res = await api.post(`/events/${eventId}/ai-generate-extra`, { type: 'welcome_board' });
      if (res.data.image_url) setPreviewWelcomeBoard(res.data.image_url);
    } catch (err) { alert('Welcome board preview failed. Please try again.'); }
    setGeneratingWelcomeBoard(false);
  };

  // Watermark overlay component
  const WatermarkOverlay = () => (
    <div style={{
      position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center',
      background: 'repeating-linear-gradient(45deg, transparent, transparent 40px, rgba(255,255,255,0.04) 40px, rgba(255,255,255,0.04) 42px)',
      pointerEvents: 'none',
    }}>
      <div style={{ background: 'rgba(0,0,0,0.6)', borderRadius: '12px', padding: '8px 16px', backdropFilter: 'blur(4px)' }}>
        <span style={{ color: 'rgba(255,255,255,0.7)', fontSize: '11px', fontWeight: 600, letterSpacing: '2px', textTransform: 'uppercase' }}>Preview Only</span>
      </div>
    </div>
  );

  // Initiate premium purchase via Paystack
  const handlePremiumPurchase = async () => {
    if (!imageUrl || premiumLoading) return;
    setPremiumLoading(true);
    try {
      const res = await api.post('/ai-design/purchase/initialize', {
        event_id: eventId,
        image_url: imageUrl,
        theme_keyword: themeKeyword,
        style: selectedStyles.join(' + '),
      });
      const { authorization_url, reference } = res.data;
      // Open Paystack in new window, then poll for completion
      const paystackWindow = window.open(authorization_url, '_blank');
      const pollInterval = setInterval(async () => {
        try {
          const verifyRes = await api.post('/ai-design/purchase/verify', { reference });
          if (verifyRes.data.clean_image_url) {
            clearInterval(pollInterval);
            setPremiumData(verifyRes.data);
            setPremiumLoading(false);
            if (paystackWindow && !paystackWindow.closed) paystackWindow.close();
          }
        } catch { /* still pending */ }
      }, 3000);
      // Stop polling after 5 minutes
      setTimeout(() => { clearInterval(pollInterval); setPremiumLoading(false); }, 300000);
    } catch (err) {
      alert('Failed to initialize payment. Please try again.');
      setPremiumLoading(false);
    }
  };

  // Premium upgrade section component
  const PremiumSection = ({ compact = false }) => {
    if (!imageUrl) return null;

    if (premiumData) {
      // Force download helper — fetches as blob to guarantee download on all devices
      const forceDownload = async (url, filename) => {
        try {
          const response = await fetch(url);
          const blob = await response.blob();
          const blobUrl = window.URL.createObjectURL(blob);
          const a = document.createElement('a');
          a.href = blobUrl;
          a.download = filename;
          document.body.appendChild(a);
          a.click();
          document.body.removeChild(a);
          window.URL.revokeObjectURL(blobUrl);
        } catch { window.open(url, '_blank'); }
      };

      // Already purchased — show download buttons
      return (
        <div style={{ background: 'linear-gradient(135deg, rgba(16,185,129,0.15), rgba(5,150,105,0.1))', borderRadius: '14px', padding: compact ? '14px' : '20px', border: '1px solid rgba(16,185,129,0.3)', marginTop: compact ? '12px' : '20px' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '12px' }}>
            <span style={{ fontSize: '20px' }}>&#10003;</span>
            <span style={{ color: '#34D399', fontWeight: 700, fontSize: compact ? '13px' : '15px' }}>Premium Unlocked!</span>
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
            <button onClick={() => forceDownload(premiumData.clean_image_url, `invitation-premium-${Date.now()}.jpg`)} style={{
              padding: compact ? '10px' : '12px 16px', borderRadius: '10px', background: '#10B981', color: 'white',
              fontSize: compact ? '12px' : '14px', fontWeight: 600, textAlign: 'center',
              display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '6px', border: 'none', cursor: 'pointer',
            }}><FiDownload size={14} /> Download Clean Invitation</button>
            {premiumData.video_url && (
              <button onClick={() => forceDownload(premiumData.video_url, `invitation-video-${Date.now()}.mp4`)} style={{
                padding: compact ? '10px' : '12px 16px', borderRadius: '10px', background: 'linear-gradient(135deg, #8B5CF6, #7C3AED)', color: 'white',
                fontSize: compact ? '12px' : '14px', fontWeight: 600, textAlign: 'center',
                display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '6px', border: 'none', cursor: 'pointer',
              }}><FiCamera size={14} /> Download AI Animated Video</button>
            )}
            {premiumData.programme_url && (
              <button onClick={() => forceDownload(premiumData.programme_url, `programme-card-${Date.now()}.jpg`)} style={{
                padding: compact ? '10px' : '12px 16px', borderRadius: '10px', background: 'linear-gradient(135deg, #F59E0B, #D97706)', color: 'white',
                fontSize: compact ? '12px' : '14px', fontWeight: 600, textAlign: 'center',
                display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '6px', border: 'none', cursor: 'pointer',
              }}><FiDownload size={14} /> Download Programme Card</button>
            )}
            {premiumData.welcome_board_url && (
              <button onClick={() => forceDownload(premiumData.welcome_board_url, `welcome-board-A1-${Date.now()}.jpg`)} style={{
                padding: compact ? '10px' : '12px 16px', borderRadius: '10px', background: 'linear-gradient(135deg, #EC4899, #DB2777)', color: 'white',
                fontSize: compact ? '12px' : '14px', fontWeight: 600, textAlign: 'center',
                display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '6px', border: 'none', cursor: 'pointer',
              }}><FiDownload size={14} /> Download A1 Welcome Board</button>
            )}
          </div>
        </div>
      );
    }

    // Not purchased — show preview + buy button
    const previewItems = [
      { key: 'invitation', icon: '\u2728', label: 'Clean Invitation', desc: 'No logo/watermark', color: '#10B981',
        hasPreview: !!imageUrl, loading: false,
        onPreview: () => setActivePreview(activePreview === 'invitation' ? null : 'invitation') },
      { key: 'video', icon: '\uD83C\uDFAC', label: 'AI Animated Video', desc: 'Motion invitation', color: '#8B5CF6',
        hasPreview: !!previewVideoUrl, loading: generatingPreview,
        onPreview: () => { if (previewVideoUrl) { setActivePreview(activePreview === 'video' ? null : 'video'); } else { generatePreview(); } } },
      { key: 'programme', icon: '\uD83D\uDCCB', label: 'Programme Card', desc: 'Day schedule', color: '#F59E0B',
        hasPreview: !!previewProgramme, loading: generatingProgramme,
        onPreview: () => { if (previewProgramme) { setActivePreview(activePreview === 'programme' ? null : 'programme'); } else { generateProgrammePreview(); } } },
      { key: 'welcome', icon: '\uD83C\uDFEA', label: 'A1 Welcome Board', desc: 'Venue entrance', color: '#EC4899',
        hasPreview: !!previewWelcomeBoard, loading: generatingWelcomeBoard,
        onPreview: () => { if (previewWelcomeBoard) { setActivePreview(activePreview === 'welcome' ? null : 'welcome'); } else { generateWelcomeBoardPreview(); } } },
    ];

    return (
      <div style={{ background: 'linear-gradient(135deg, rgba(139,92,246,0.12), rgba(124,58,237,0.08))', borderRadius: '14px', padding: compact ? '14px' : '20px', border: '1px solid rgba(139,92,246,0.25)', marginTop: compact ? '12px' : '20px' }}>
        {/* Go Premium header + unlock button removed — replaced by AI Event Media Package card in the Invitations tab */}
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '8px' }}>
          <span style={{ fontSize: '18px' }}>&#9733;</span>
          <span style={{ color: '#C4B5FD', fontWeight: 700, fontSize: compact ? '14px' : '16px', fontFamily: "'Playfair Display', serif" }}>Go Premium</span>
          <span style={{ marginLeft: 'auto', background: 'rgba(139,92,246,0.3)', borderRadius: '20px', padding: '3px 10px', fontSize: '12px', fontWeight: 700, color: '#DDD6FE' }}>R280</span>
        </div>
        <p style={{ color: 'rgba(255,255,255,0.5)', fontSize: compact ? '11px' : '12px', marginBottom: '8px', lineHeight: 1.5 }}>
          All items match your selected invitation's style:
        </p>
        {savedDesigns.length > 1 && (
          <div onClick={() => setShowSaved(!showSaved)} style={{
            display: 'flex', alignItems: 'center', gap: '8px', padding: '6px 10px', borderRadius: '8px',
            background: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.1)',
            cursor: 'pointer', marginBottom: '10px',
          }}>
            <img src={imageUrl} alt="" style={{ width: '28px', height: '50px', borderRadius: '4px', objectFit: 'cover' }} />
            <div style={{ flex: 1 }}>
              <span style={{ color: 'rgba(255,255,255,0.7)', fontSize: '11px', fontWeight: 600 }}>Selected Design {historyIndex + 1}</span>
              <span style={{ color: 'rgba(255,255,255,0.35)', fontSize: '10px', display: 'block' }}>Tap to change</span>
            </div>
            <FiImage size={14} style={{ color: 'rgba(255,255,255,0.3)' }} />
          </div>
        )}

        {/* Package items — clickable preview cards */}
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: compact ? '6px' : '8px', marginBottom: '12px' }}>
          {previewItems.map((item) => (
            <div key={item.key} onClick={item.loading ? undefined : item.onPreview} style={{
              background: activePreview === item.key ? `${item.color}22` : `${item.color}11`,
              border: `1px solid ${activePreview === item.key ? item.color : item.color + '33'}`,
              borderRadius: '10px', padding: compact ? '8px' : '10px', textAlign: 'center',
              cursor: item.loading ? 'wait' : 'pointer', transition: 'all 0.2s',
              position: 'relative', overflow: 'hidden',
            }}>
              {item.loading && (
                <div style={{ position: 'absolute', inset: 0, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', borderRadius: '10px' }}>
                  <FiRefreshCw size={16} style={{ animation: 'spin 1s linear infinite', color: item.color }} />
                </div>
              )}
              <div style={{ fontSize: compact ? '18px' : '22px', marginBottom: '4px' }}>{item.icon}</div>
              <div style={{ color: item.color, fontSize: compact ? '10px' : '11px', fontWeight: 700, lineHeight: 1.2 }}>{item.label}</div>
              <div style={{ color: 'rgba(255,255,255,0.35)', fontSize: compact ? '9px' : '10px', marginTop: '2px' }}>
                {item.hasPreview ? 'Tap to view' : item.loading ? 'Generating...' : 'Tap to preview'}
              </div>
              {item.hasPreview && <div style={{ width: '6px', height: '6px', borderRadius: '50%', background: item.color, margin: '4px auto 0' }} />}
            </div>
          ))}
        </div>

        {/* Active preview display */}
        {activePreview === 'invitation' && imageUrl && (
          <div style={{ position: 'relative', borderRadius: '10px', overflow: 'hidden', marginBottom: '12px' }}>
            <img src={imageUrl} alt="Clean invitation preview" style={{ width: '100%', borderRadius: '10px', display: 'block' }} />
            <WatermarkOverlay />
          </div>
        )}

        {activePreview === 'video' && previewVideoUrl && (
          <div style={{ position: 'relative', borderRadius: '10px', overflow: 'hidden', marginBottom: '12px' }}>
            <video src={previewVideoUrl} autoPlay loop playsInline controls controlsList="nodownload" style={{ width: '100%', borderRadius: '10px', display: 'block' }} />
            <WatermarkOverlay />
            <p style={{ color: 'rgba(255,255,255,0.35)', fontSize: '10px', textAlign: 'center', marginTop: '4px' }}>Preview only — unlock premium to download without watermark</p>
          </div>
        )}

        {activePreview === 'programme' && previewProgramme && (
          <div style={{ position: 'relative', borderRadius: '10px', overflow: 'hidden', marginBottom: '12px' }}>
            <img src={previewProgramme} alt="Programme card preview" style={{ width: '100%', borderRadius: '10px', display: 'block' }} />
            <WatermarkOverlay />
          </div>
        )}

        {activePreview === 'welcome' && previewWelcomeBoard && (
          <div style={{ position: 'relative', borderRadius: '10px', overflow: 'hidden', marginBottom: '12px' }}>
            <img src={previewWelcomeBoard} alt="Welcome board preview" style={{ width: '100%', borderRadius: '10px', display: 'block' }} />
            <WatermarkOverlay />
          </div>
        )}

        {/* Loading state for any generating preview */}
        {(generatingPreview || generatingProgramme || generatingWelcomeBoard) && !activePreview?.includes('invitation') && (
          <div style={{ textAlign: 'center', padding: '16px 0', marginBottom: '12px' }}>
            <FiRefreshCw size={20} style={{ animation: 'spin 1s linear infinite', color: '#C4B5FD' }} />
            <p style={{ color: 'rgba(255,255,255,0.4)', fontSize: '12px', marginTop: '8px' }}>
              Generating {generatingPreview ? 'video' : generatingProgramme ? 'programme card' : 'welcome board'} preview...
            </p>
          </div>
        )}

      </div>
    );
  };

  // Shared action buttons component
  const ActionButtons = ({ size = 'normal', direction = 'row' }) => {
    const isSmall = size === 'small';
    const pad = isSmall ? '10px 14px' : '13px 16px';
    const fs = isSmall ? '12px' : '14px';
    const iconSize = isSmall ? 13 : 15;
    return (
      <div style={{ display: 'flex', flexDirection: direction, gap: isSmall ? '6px' : '8px', width: '100%' }}>
        <button onClick={applyAsInvitation} style={{
          flex: 1, padding: pad, borderRadius: '10px', border: 'none',
          background: 'linear-gradient(135deg, #10B981, #059669)', color: 'white',
          fontSize: fs, fontWeight: 600, cursor: 'pointer',
          display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '6px',
        }}><FiCheck size={iconSize} /> Use as Invitation</button>
        <button onClick={downloadImage} style={{
          flex: 1, padding: pad, borderRadius: '10px',
          border: '1px solid rgba(255,255,255,0.2)', background: 'rgba(255,255,255,0.06)',
          color: 'rgba(255,255,255,0.8)', fontSize: fs, fontWeight: 500, cursor: 'pointer',
          display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '6px',
        }}><FiDownload size={iconSize} /> Download</button>
        <button onClick={openFullscreen} style={{
          padding: pad, borderRadius: '10px',
          border: '1px solid rgba(255,255,255,0.2)', background: 'rgba(255,255,255,0.06)',
          color: 'rgba(255,255,255,0.8)', fontSize: fs, fontWeight: 500, cursor: 'pointer',
          display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '6px',
          ...(direction === 'row' ? { minWidth: 'auto' } : {}),
        }}><FiMaximize2 size={iconSize} />{direction === 'column' ? ' View Full Screen' : ''}</button>
      </div>
    );
  };

  return (
    <div style={{ position: 'fixed', inset: 0, zIndex: 9998, background: 'rgba(0,0,0,0.92)', display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
      {/* Header */}
      <div style={{ padding: '12px 20px', display: 'flex', alignItems: 'center', justifyContent: 'space-between', borderBottom: '1px solid rgba(255,255,255,0.1)', flexShrink: 0 }}>
        <div>
          <h2 style={{ color: 'white', fontSize: '18px', fontWeight: 700, margin: 0, fontFamily: "'Playfair Display', serif" }}>AI Invitation Designer</h2>
          <p style={{ color: 'rgba(255,255,255,0.4)', fontSize: '11px', margin: '2px 0 0' }}>Powered by GiGi AI</p>
        </div>
        <button onClick={onClose} style={{ background: 'none', border: 'none', color: 'rgba(255,255,255,0.5)', fontSize: '28px', cursor: 'pointer', lineHeight: 1, padding: '4px 8px' }}>&times;</button>
      </div>

      {/* Desktop layout */}
      <div style={{ flex: 1, display: 'flex', overflow: 'hidden' }} className="ai-designer-desktop">
        {/* Left panel — settings */}
        <div className="ai-designer-left" style={{ width: '340px', flexShrink: 0, padding: '24px', overflowY: 'auto', borderRight: '1px solid rgba(255,255,255,0.08)', background: 'rgba(255,255,255,0.02)' }}>
          <h3 style={{ color: '#E3425C', fontSize: '14px', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '1.5px', marginBottom: '20px' }}>Design Preferences</h3>

          {/* Style selector — multi-select toggle tags */}
          <div style={{ marginBottom: '20px' }}>
            <label style={{ color: 'rgba(255,255,255,0.6)', fontSize: '12px', fontWeight: 600, display: 'block', marginBottom: '8px', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Styles <span style={{ color: '#E3425C', fontSize: '10px', fontWeight: 400, textTransform: 'none', letterSpacing: '0' }}>&mdash; tap to combine (min 1)</span></label>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px' }}>
              {STYLES.map(s => {
                const isRecommended = recommendedStyles.includes(s.id);
                const isSelected = selectedStyles.includes(s.id);
                return (
                  <button key={s.id} onClick={() => toggleStyle(s.id)} style={{
                    padding: '7px 14px', borderRadius: '20px', fontSize: '12px', fontWeight: 500, cursor: 'pointer',
                    border: `1px solid ${isSelected ? '#E3425C' : isRecommended ? 'rgba(227,66,92,0.4)' : 'rgba(255,255,255,0.12)'}`,
                    background: isSelected ? '#E3425C22' : isRecommended ? 'rgba(227,66,92,0.06)' : 'transparent',
                    color: isSelected ? '#E3425C' : isRecommended ? 'rgba(227,66,92,0.7)' : 'rgba(255,255,255,0.5)',
                    transition: 'all 0.2s', position: 'relative',
                  }}>
                    {s.icon && <span style={{ marginRight: '4px' }}>{s.icon}</span>}{s.label}
                    {isSelected && <span style={{ marginLeft: '4px', fontSize: '10px' }}>&#10003;</span>}
                    {isRecommended && !isSelected && (
                      <span style={{
                        position: 'absolute', top: '-4px', right: '-4px',
                        width: '6px', height: '6px', borderRadius: '50%',
                        background: '#E3425C',
                      }} />
                    )}
                  </button>
                );
              })}
            </div>
          </div>

          {/* Color scheme background toggle */}
          <label style={{
            display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '20px',
            padding: '10px 14px', borderRadius: '10px',
            background: useColorSchemeBg ? 'rgba(227,66,92,0.08)' : 'rgba(255,255,255,0.03)',
            border: `1px solid ${useColorSchemeBg ? 'rgba(227,66,92,0.25)' : 'rgba(255,255,255,0.08)'}`,
            cursor: 'pointer', transition: 'all 0.2s',
          }}>
            <input
              type="checkbox"
              checked={useColorSchemeBg}
              onChange={(e) => setUseColorSchemeBg(e.target.checked)}
              style={{ accentColor: '#E3425C', width: '16px', height: '16px', cursor: 'pointer' }}
            />
            <span style={{ color: 'rgba(255,255,255,0.7)', fontSize: '12px', fontWeight: 500 }}>
              Use color palette as background
            </span>
          </label>

          {/* Theme input */}
          <div style={{ marginBottom: '20px' }}>
            <label style={{ color: 'rgba(255,255,255,0.6)', fontSize: '12px', fontWeight: 600, display: 'block', marginBottom: '8px', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Theme / Keyword</label>
            <input
              value={themeKeyword}
              onChange={e => setThemeKeyword(e.target.value)}
              placeholder="e.g., Barbie, Vintage, Gold & Black..."
              style={{
                width: '100%', padding: '12px 16px', borderRadius: '10px', border: '1px solid rgba(255,255,255,0.12)',
                background: 'rgba(255,255,255,0.05)', color: 'white', fontSize: '14px', outline: 'none', boxSizing: 'border-box',
              }}
            />
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '4px', marginTop: '8px' }}>
              {[...suggestions, ...(['Gold & Black', 'Floral', 'Afro-chic', 'Pastel', 'Neon'].filter(s => !suggestions.includes(s)))].slice(0, 12).map(s => (
                <button key={s} onClick={() => setThemeKeyword(s)} style={{
                  padding: '4px 10px', borderRadius: '12px', fontSize: '11px', cursor: 'pointer',
                  border: '1px solid rgba(255,255,255,0.08)', background: themeKeyword === s ? 'rgba(227,66,92,0.15)' : 'transparent',
                  color: themeKeyword === s ? '#E3425C' : 'rgba(255,255,255,0.35)', transition: 'all 0.2s',
                }}>{s}</button>
              ))}
            </div>
          </div>

          {/* Invitation Heading */}
          <div style={{ marginBottom: '20px' }}>
            <label style={{ color: 'rgba(255,255,255,0.6)', fontSize: '12px', fontWeight: 600, display: 'block', marginBottom: '8px', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Invitation Heading</label>
            <select value={headingOption} onChange={e => setHeadingOption(e.target.value)} style={{
              width: '100%', padding: '12px 16px', borderRadius: '10px', border: '1px solid rgba(255,255,255,0.12)',
              background: 'rgba(255,255,255,0.05)', color: 'white', fontSize: '14px', outline: 'none', boxSizing: 'border-box',
              appearance: 'auto',
            }}>
              <option value="" style={{ background: '#1a1a2e' }}>You're Invited to (default)</option>
              <option value="You Are Cordially Invited to" style={{ background: '#1a1a2e' }}>You Are Cordially Invited to</option>
              <option value="You're Warmly Invited to" style={{ background: '#1a1a2e' }}>You're Warmly Invited to</option>
              <option value="We Request the Honour of Your Presence at" style={{ background: '#1a1a2e' }}>We Request the Honour of Your Presence at</option>
              <option value="Join Us in Celebrating" style={{ background: '#1a1a2e' }}>Join Us in Celebrating</option>
              <option value="Save the Date" style={{ background: '#1a1a2e' }}>Save the Date</option>
              <option value="In Loving Memory of" style={{ background: '#1a1a2e' }}>In Loving Memory of</option>
              <option value="Umemiwe" style={{ background: '#1a1a2e' }}>Umemiwe (Zulu)</option>
              <option value="O Memeletswe" style={{ background: '#1a1a2e' }}>O Memeletswe (Sotho/Tswana)</option>
              <option value="U Rambiwa" style={{ background: '#1a1a2e' }}>U Rambiwa (Venda)</option>
              <option value="custom" style={{ background: '#1a1a2e' }}>Custom...</option>
            </select>
            {(headingOption === 'custom' || (headingOption && !['', 'You Are Cordially Invited to', "You're Warmly Invited to", 'We Request the Honour of Your Presence at', 'Join Us in Celebrating', 'Save the Date', 'In Loving Memory of', 'Umemiwe', 'O Memeletswe', 'U Rambiwa'].includes(headingOption))) && (
              <input value={headingOption === 'custom' ? '' : headingOption} onChange={e => setHeadingOption(e.target.value)} placeholder="e.g., Woza Sizobungaza..." style={{
                width: '100%', padding: '10px 14px', borderRadius: '10px', border: '1px solid rgba(255,255,255,0.12)',
                background: 'rgba(255,255,255,0.05)', color: 'white', fontSize: '13px', outline: 'none', boxSizing: 'border-box', marginTop: '8px',
              }} />
            )}
          </div>

          {/* Photo upload */}
          <div style={{ marginBottom: '16px' }}>
            <label style={{ color: 'rgba(255,255,255,0.6)', fontSize: '12px', fontWeight: 600, display: 'block', marginBottom: '6px', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Photo (Optional)</label>
            <p style={{ color: 'rgba(255,255,255,0.3)', fontSize: '11px', marginBottom: '8px' }}>Include a photo of the person/couple</p>
            {!photoPreview ? (
              <button onClick={() => fileInputRef.current?.click()} style={{
                width: '100%', padding: '12px', borderRadius: '10px', cursor: 'pointer',
                border: '1px dashed rgba(255,255,255,0.2)', background: 'rgba(255,255,255,0.03)',
                color: 'rgba(255,255,255,0.4)', fontSize: '12px', fontWeight: 500,
                display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px',
              }}>
                <FiCamera size={14} /> Upload Photo
              </button>
            ) : (
              <div style={{ position: 'relative', borderRadius: '10px', overflow: 'hidden' }}>
                <img src={photoPreview} alt="Uploaded" style={{ width: '100%', height: '90px', objectFit: 'cover', borderRadius: '10px' }} />
                <button onClick={removePhoto} style={{
                  position: 'absolute', top: '4px', right: '4px',
                  width: '22px', height: '22px', borderRadius: '50%',
                  background: 'rgba(0,0,0,0.7)', border: 'none', color: 'white',
                  cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center',
                }}><FiX size={12} /></button>
              </div>
            )}
            <input ref={fileInputRef} type="file" accept="image/*" onChange={handlePhotoUpload} style={{ display: 'none' }} />
          </div>

          {/* Inspiration image upload */}
          <div style={{ marginBottom: '20px' }}>
            <label style={{ color: 'rgba(255,255,255,0.6)', fontSize: '12px', fontWeight: 600, display: 'block', marginBottom: '6px', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Inspiration Image (Optional)</label>
            <p style={{ color: 'rgba(255,255,255,0.3)', fontSize: '11px', marginBottom: '8px' }}>Upload a design you like — AI will match its style</p>
            {!inspirationPreview ? (
              <button onClick={() => inspoInputRef.current?.click()} style={{
                width: '100%', padding: '12px', borderRadius: '10px', cursor: 'pointer',
                border: '1px dashed rgba(255,255,255,0.2)', background: 'rgba(255,255,255,0.03)',
                color: 'rgba(255,255,255,0.4)', fontSize: '12px', fontWeight: 500,
                display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px',
              }}>
                <FiImage size={14} /> Upload Inspiration
              </button>
            ) : (
              <div style={{ position: 'relative', borderRadius: '10px', overflow: 'hidden' }}>
                <img src={inspirationPreview} alt="Inspiration" style={{ width: '100%', height: '90px', objectFit: 'cover', borderRadius: '10px' }} />
                <button onClick={removeInspiration} style={{
                  position: 'absolute', top: '4px', right: '4px',
                  width: '22px', height: '22px', borderRadius: '50%',
                  background: 'rgba(0,0,0,0.7)', border: 'none', color: 'white',
                  cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center',
                }}><FiX size={12} /></button>
                <div style={{ position: 'absolute', bottom: '4px', left: '4px', background: 'rgba(0,0,0,0.6)', borderRadius: '6px', padding: '2px 8px', fontSize: '10px', color: 'rgba(255,255,255,0.7)' }}>
                  Style reference
                </div>
              </div>
            )}
            <input ref={inspoInputRef} type="file" accept="image/*" onChange={handleInspirationUpload} style={{ display: 'none' }} />
          </div>

          {/* Generate + Preview buttons */}
          <div style={{ display: 'flex', gap: '8px' }}>
            <button onClick={generateDesignPreview} disabled={loading || generatingDesignPreview} style={{
              flex: 1, padding: '14px', borderRadius: '12px', border: '1px solid rgba(255,255,255,0.2)',
              background: generatingDesignPreview ? 'rgba(255,255,255,0.05)' : 'transparent',
              color: generatingDesignPreview ? 'rgba(255,255,255,0.4)' : 'rgba(255,255,255,0.7)',
              fontSize: '14px', fontWeight: 600, cursor: (loading || generatingDesignPreview) ? 'not-allowed' : 'pointer',
              display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '6px',
            }}>
              {generatingDesignPreview ? <><FiRefreshCw size={14} style={{ animation: 'spin 1s linear infinite' }} /> Previewing...</> : <><FiEye size={14} /> Preview</>}
            </button>
            <button onClick={generateDesign} disabled={loading || generatingDesignPreview} style={{
              flex: 2, padding: '14px', borderRadius: '12px', border: 'none',
              background: loading ? 'rgba(227,66,92,0.3)' : 'linear-gradient(135deg, #E3425C, #D13A4F)',
              color: loading ? 'rgba(255,255,255,0.5)' : '#ffffff', fontSize: '15px', fontWeight: 700,
              cursor: (loading || generatingDesignPreview) ? 'not-allowed' : 'pointer',
              display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px',
            }}>
              {loading ? <><FiRefreshCw size={16} style={{ animation: 'spin 1s linear infinite' }} /> Generating...</> : <><FiZap size={16} /> Generate Design</>}
            </button>
          </div>

          {loading && <p style={{ textAlign: 'center', color: 'rgba(255,255,255,0.3)', fontSize: '12px', marginTop: '10px', fontStyle: 'italic' }}>AI is creating your image... 10-15 seconds</p>}
          {error && <div style={{ marginTop: '12px', padding: '12px 16px', borderRadius: '10px', background: 'rgba(239,68,68,0.12)', border: '1px solid rgba(239,68,68,0.25)', color: '#FCA5A5', fontSize: '13px' }}>{error}</div>}

          {/* History navigation */}
          {history.length > 1 && (
            <div style={{ display: 'flex', gap: '8px', marginTop: '12px' }}>
              <button onClick={goPrevious} disabled={historyIndex <= 0} style={{ flex: 1, padding: '10px', borderRadius: '8px', border: '1px solid rgba(255,255,255,0.1)', background: 'transparent', color: historyIndex <= 0 ? 'rgba(255,255,255,0.2)' : 'rgba(255,255,255,0.6)', cursor: historyIndex <= 0 ? 'not-allowed' : 'pointer', fontSize: '13px' }}>{'\u2190'} Previous</button>
              <button onClick={goNext} disabled={historyIndex >= history.length - 1} style={{ flex: 1, padding: '10px', borderRadius: '8px', border: '1px solid rgba(255,255,255,0.1)', background: 'transparent', color: historyIndex >= history.length - 1 ? 'rgba(255,255,255,0.2)' : 'rgba(255,255,255,0.6)', cursor: historyIndex >= history.length - 1 ? 'not-allowed' : 'pointer', fontSize: '13px' }}>Next {'\u2192'}</button>
            </div>
          )}
          {history.length > 0 && <p style={{ textAlign: 'center', color: 'rgba(255,255,255,0.25)', fontSize: '11px', marginTop: '8px' }}>Design {historyIndex + 1} of {history.length}</p>}

          {/* Saved designs gallery toggle */}
          {savedDesigns.length > 0 && (
            <div style={{ marginTop: '12px' }}>
              <button onClick={() => setShowSaved(!showSaved)} style={{
                width: '100%', padding: '10px', borderRadius: '10px',
                border: '1px solid rgba(255,255,255,0.1)', background: showSaved ? 'rgba(255,255,255,0.08)' : 'transparent',
                color: 'rgba(255,255,255,0.6)', cursor: 'pointer', fontFamily: 'Inter, sans-serif', fontSize: '13px', fontWeight: 500,
                display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '6px',
              }}><FiImage size={14} /> My Designs ({savedDesigns.length})</button>
              {showSaved && (
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '6px', marginTop: '10px', maxHeight: '240px', overflowY: 'auto' }}>
                  {savedDesigns.map((d, idx) => (
                    <div key={idx} onClick={() => { setImageUrl(d.image_url); setHistoryIndex(idx); setShowSaved(false); setPreviewVideoUrl(null); setPremiumData(null); if (d.theme_keyword) setThemeKeyword(d.theme_keyword); if (d.style) setSelectedStyles([d.style]); }} style={{
                      borderRadius: '8px', overflow: 'hidden', cursor: 'pointer',
                      border: imageUrl === d.image_url ? '2px solid #E3425C' : '2px solid transparent',
                      opacity: imageUrl === d.image_url ? 1 : 0.7,
                      transition: 'all 0.2s',
                    }}>
                      <img src={d.image_url} alt={`Design ${idx + 1}`} style={{ width: '100%', display: 'block', aspectRatio: '9/16', objectFit: 'cover' }} />
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* Desktop action buttons */}
          {imageUrl && (
            <div style={{ marginTop: '20px' }}>
              <ActionButtons size="normal" direction="column" />
              <PremiumSection compact={false} />
            </div>
          )}
        </div>

        {/* Right panel — desktop preview */}
        <div className="ai-designer-right" style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '32px', overflowY: 'auto', background: 'rgba(0,0,0,0.3)' }}>
          {loading ? (
            <div style={{ textAlign: 'center', color: 'rgba(255,255,255,0.5)' }}>
              <div style={{ width: '60px', height: '60px', border: '3px solid rgba(227,66,92,0.2)', borderTopColor: '#E3425C', borderRadius: '50%', animation: 'spin 1s linear infinite', margin: '0 auto 20px' }} />
              <h3 style={{ fontFamily: "'Playfair Display', serif", fontSize: '22px', marginBottom: '8px', color: 'rgba(255,255,255,0.6)' }}>Creating Your Invitation...</h3>
              <p style={{ fontSize: '14px', color: 'rgba(255,255,255,0.35)' }}>Our AI is designing a unique invitation just for you</p>
            </div>
          ) : generatingDesignPreview ? (
            <div style={{ textAlign: 'center', color: 'rgba(255,255,255,0.5)' }}>
              <div style={{ width: '50px', height: '50px', border: '3px solid rgba(255,255,255,0.15)', borderTopColor: 'rgba(255,255,255,0.6)', borderRadius: '50%', animation: 'spin 1s linear infinite', margin: '0 auto 16px' }} />
              <h3 style={{ fontFamily: "'Playfair Display', serif", fontSize: '20px', marginBottom: '6px', color: 'rgba(255,255,255,0.5)' }}>Generating Preview...</h3>
              <p style={{ fontSize: '13px', color: 'rgba(255,255,255,0.3)' }}>Quick preview so you can check the look</p>
            </div>
          ) : designPreviewUrl && !imageUrl ? (
            <div style={{ textAlign: 'center', maxWidth: '100%', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '16px' }}>
              <div style={{ position: 'relative' }}>
                <img src={designPreviewUrl} alt="Preview" style={{
                  maxWidth: '100%', maxHeight: 'calc(100vh - 300px)', borderRadius: '14px',
                  boxShadow: '0 16px 48px rgba(0,0,0,0.45)', objectFit: 'contain',
                }} />
                <div style={{
                  position: 'absolute', top: '8px', left: '8px',
                  background: 'rgba(0,0,0,0.65)', borderRadius: '8px', padding: '4px 12px',
                  fontSize: '12px', color: 'rgba(255,255,255,0.8)', fontWeight: 600,
                  backdropFilter: 'blur(4px)',
                }}>Preview</div>
              </div>
              <p style={{ fontSize: '13px', color: 'rgba(255,255,255,0.4)' }}>This is a quick preview without text overlay. Full version will have event details.</p>
              <div style={{ display: 'flex', gap: '10px', width: '100%', maxWidth: '360px' }}>
                <button onClick={() => { setDesignPreviewUrl(null); }} style={{
                  flex: 1, padding: '12px', borderRadius: '10px', border: '1px solid rgba(255,255,255,0.15)',
                  background: 'transparent', color: 'rgba(255,255,255,0.7)', fontSize: '14px', fontWeight: 600,
                  cursor: 'pointer', fontFamily: 'Inter, sans-serif',
                }}>Try Again</button>
                <button onClick={() => { setDesignPreviewUrl(null); generateDesign(); }} style={{
                  flex: 2, padding: '12px', borderRadius: '10px', border: 'none',
                  background: 'linear-gradient(135deg, #E3425C, #D13A4F)',
                  color: '#ffffff', fontSize: '14px', fontWeight: 700,
                  cursor: 'pointer', fontFamily: 'Inter, sans-serif',
                  display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '6px',
                }}><FiCheck size={16} /> Looks Good — Generate Full</button>
              </div>
            </div>
          ) : !imageUrl ? (
            <div style={{ textAlign: 'center', color: 'rgba(255,255,255,0.3)' }}>
              <div style={{ fontSize: '60px', marginBottom: '16px' }}>&#127912;</div>
              <h3 style={{ fontFamily: "'Playfair Display', serif", fontSize: '22px', marginBottom: '8px', color: 'rgba(255,255,255,0.5)' }}>AI Image Preview</h3>
              <p style={{ fontSize: '14px' }}>Choose a style and theme, then click "Generate Design"</p>
            </div>
          ) : (
            <div style={{ textAlign: 'center', maxWidth: '100%', cursor: 'pointer' }} onClick={openFullscreen}>
              <img src={imageUrl} alt="AI Generated Invitation" style={{
                maxWidth: '100%', maxHeight: 'calc(100vh - 160px)', borderRadius: '16px',
                boxShadow: '0 20px 60px rgba(0,0,0,0.5), 0 0 0 1px rgba(255,255,255,0.06)',
                objectFit: 'contain',
              }} />
              <p style={{ marginTop: '10px', fontSize: '11px', color: 'rgba(255,255,255,0.25)' }}>Click to view full screen</p>
            </div>
          )}
        </div>
      </div>

      {/* ===== MOBILE LAYOUT ===== */}
      <div className="ai-designer-mobile" style={{ display: 'none', flex: 1, flexDirection: 'column', overflow: 'hidden' }}>
        <div style={{ flex: 1, overflowY: 'auto', padding: '16px', WebkitOverflowScrolling: 'touch' }}>

          {/* If image exists, show it FIRST on mobile with action buttons */}
          {imageUrl && !loading && (
            <div style={{ marginBottom: '20px' }}>
              <div style={{ borderRadius: '14px', overflow: 'hidden', marginBottom: '12px', position: 'relative' }} onClick={openFullscreen}>
                <img src={imageUrl} alt="AI Generated Invitation" style={{
                  width: '100%', borderRadius: '14px',
                  boxShadow: '0 10px 40px rgba(0,0,0,0.4)',
                }} />
                <div style={{
                  position: 'absolute', bottom: '8px', right: '8px',
                  background: 'rgba(0,0,0,0.6)', borderRadius: '8px', padding: '4px 10px',
                  fontSize: '10px', color: 'rgba(255,255,255,0.7)', backdropFilter: 'blur(4px)',
                }}>Tap to enlarge</div>
              </div>

              {/* Big action buttons on mobile */}
              <ActionButtons size="small" direction="row" />

              {/* History navigation on mobile */}
              {history.length > 1 && (
                <div style={{ display: 'flex', gap: '8px', marginTop: '10px' }}>
                  <button onClick={goPrevious} disabled={historyIndex <= 0} style={{ flex: 1, padding: '8px', borderRadius: '8px', border: '1px solid rgba(255,255,255,0.1)', background: 'transparent', color: historyIndex <= 0 ? 'rgba(255,255,255,0.2)' : 'rgba(255,255,255,0.6)', fontSize: '12px' }}>{'\u2190'} Prev</button>
                  <span style={{ color: 'rgba(255,255,255,0.25)', fontSize: '11px', display: 'flex', alignItems: 'center' }}>{historyIndex + 1}/{history.length}</span>
                  <button onClick={goNext} disabled={historyIndex >= history.length - 1} style={{ flex: 1, padding: '8px', borderRadius: '8px', border: '1px solid rgba(255,255,255,0.1)', background: 'transparent', color: historyIndex >= history.length - 1 ? 'rgba(255,255,255,0.2)' : 'rgba(255,255,255,0.6)', fontSize: '12px' }}>Next {'\u2192'}</button>
                </div>
              )}

              {/* Saved designs gallery on mobile */}
              {savedDesigns.length > 1 && (
                <div style={{ marginTop: '10px' }}>
                  <button onClick={() => setShowSaved(!showSaved)} style={{
                    width: '100%', padding: '8px', borderRadius: '8px',
                    border: '1px solid rgba(255,255,255,0.1)', background: showSaved ? 'rgba(255,255,255,0.08)' : 'transparent',
                    color: 'rgba(255,255,255,0.5)', cursor: 'pointer', fontSize: '12px', fontWeight: 500,
                  }}><FiImage size={12} style={{ marginRight: '4px', verticalAlign: '-2px' }} />My Designs ({savedDesigns.length})</button>
                  {showSaved && (
                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '4px', marginTop: '8px', maxHeight: '160px', overflowY: 'auto' }}>
                      {savedDesigns.map((d, idx) => (
                        <div key={idx} onClick={() => { setImageUrl(d.image_url); setHistoryIndex(idx); setShowSaved(false); setPreviewVideoUrl(null); setPremiumData(null); if (d.theme_keyword) setThemeKeyword(d.theme_keyword); if (d.style) setSelectedStyles([d.style]); }} style={{
                          borderRadius: '6px', overflow: 'hidden', cursor: 'pointer',
                          border: imageUrl === d.image_url ? '2px solid #E3425C' : '2px solid transparent',
                          opacity: imageUrl === d.image_url ? 1 : 0.65,
                        }}>
                          <img src={d.image_url} alt={`Design ${idx + 1}`} style={{ width: '100%', display: 'block', aspectRatio: '9/16', objectFit: 'cover' }} />
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}

              <div style={{ height: '1px', background: 'rgba(255,255,255,0.08)', margin: '16px 0' }} />
              <p style={{ color: 'rgba(255,255,255,0.35)', fontSize: '12px', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '1px', marginBottom: '12px' }}>Generate Another</p>
            </div>
          )}

          {/* Preview result on mobile */}
          {designPreviewUrl && !imageUrl && !loading && !generatingDesignPreview && (
            <div style={{ marginBottom: '20px', textAlign: 'center' }}>
              <div style={{ position: 'relative', borderRadius: '14px', overflow: 'hidden', marginBottom: '12px' }}>
                <img src={designPreviewUrl} alt="Preview" style={{
                  width: '100%', borderRadius: '14px',
                  boxShadow: '0 10px 40px rgba(0,0,0,0.4)',
                }} />
                <div style={{
                  position: 'absolute', top: '8px', left: '8px',
                  background: 'rgba(0,0,0,0.65)', borderRadius: '8px', padding: '4px 12px',
                  fontSize: '12px', color: 'rgba(255,255,255,0.8)', fontWeight: 600,
                  backdropFilter: 'blur(4px)',
                }}>Preview</div>
              </div>
              <p style={{ fontSize: '12px', color: 'rgba(255,255,255,0.4)', marginBottom: '12px' }}>Quick preview without text overlay</p>
              <div style={{ display: 'flex', gap: '10px' }}>
                <button onClick={() => setDesignPreviewUrl(null)} style={{
                  flex: 1, padding: '12px', borderRadius: '10px', border: '1px solid rgba(255,255,255,0.15)',
                  background: 'transparent', color: 'rgba(255,255,255,0.7)', fontSize: '14px', fontWeight: 600,
                  cursor: 'pointer', fontFamily: 'Inter, sans-serif',
                }}>Try Again</button>
                <button onClick={() => { setDesignPreviewUrl(null); generateDesign(); }} style={{
                  flex: 2, padding: '12px', borderRadius: '10px', border: 'none',
                  background: 'linear-gradient(135deg, #E3425C, #D13A4F)',
                  color: '#ffffff', fontSize: '14px', fontWeight: 700,
                  cursor: 'pointer', fontFamily: 'Inter, sans-serif',
                  display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '6px',
                }}><FiCheck size={16} /> Looks Good — Generate</button>
              </div>
              <div style={{ height: '1px', background: 'rgba(255,255,255,0.08)', margin: '16px 0' }} />
            </div>
          )}

          {/* Loading state on mobile */}
          {loading && (
            <div style={{ textAlign: 'center', padding: '40px 0', marginBottom: '20px' }}>
              <div style={{ width: '50px', height: '50px', border: '3px solid rgba(227,66,92,0.2)', borderTopColor: '#E3425C', borderRadius: '50%', animation: 'spin 1s linear infinite', margin: '0 auto 16px' }} />
              <h3 style={{ fontFamily: "'Playfair Display', serif", fontSize: '18px', color: 'rgba(255,255,255,0.6)', marginBottom: '6px' }}>Creating Your Invitation...</h3>
              <p style={{ fontSize: '13px', color: 'rgba(255,255,255,0.35)' }}>This may take 10-15 seconds</p>
            </div>
          )}

          {/* Mobile settings — always visible below image */}
          <div style={{ marginBottom: '16px' }}>
            <label style={{ color: 'rgba(255,255,255,0.6)', fontSize: '11px', fontWeight: 600, display: 'block', marginBottom: '8px', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Styles <span style={{ color: '#E3425C', fontSize: '9px', fontWeight: 400, textTransform: 'none', letterSpacing: '0' }}>&mdash; tap to combine (min 1)</span></label>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '5px' }}>
              {STYLES.map(s => {
                const isRecommended = recommendedStyles.includes(s.id);
                const isSelected = selectedStyles.includes(s.id);
                return (
                  <button key={s.id} onClick={() => toggleStyle(s.id)} style={{
                    padding: '6px 12px', borderRadius: '16px', fontSize: '11px', fontWeight: 500, cursor: 'pointer',
                    border: `1px solid ${isSelected ? '#E3425C' : isRecommended ? 'rgba(227,66,92,0.4)' : 'rgba(255,255,255,0.12)'}`,
                    background: isSelected ? '#E3425C22' : isRecommended ? 'rgba(227,66,92,0.06)' : 'transparent',
                    color: isSelected ? '#E3425C' : isRecommended ? 'rgba(227,66,92,0.7)' : 'rgba(255,255,255,0.5)',
                    position: 'relative',
                  }}>
                    {s.icon && <span style={{ marginRight: '3px' }}>{s.icon}</span>}{s.label}
                    {isSelected && <span style={{ marginLeft: '3px', fontSize: '9px' }}>&#10003;</span>}
                    {isRecommended && !isSelected && (
                      <span style={{
                        position: 'absolute', top: '-3px', right: '-3px',
                        width: '5px', height: '5px', borderRadius: '50%',
                        background: '#E3425C',
                      }} />
                    )}
                  </button>
                );
              })}
            </div>
          </div>

          {/* Color scheme background toggle — mobile */}
          <label style={{
            display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '16px',
            padding: '8px 12px', borderRadius: '10px',
            background: useColorSchemeBg ? 'rgba(227,66,92,0.08)' : 'rgba(255,255,255,0.03)',
            border: `1px solid ${useColorSchemeBg ? 'rgba(227,66,92,0.25)' : 'rgba(255,255,255,0.08)'}`,
            cursor: 'pointer', transition: 'all 0.2s',
          }}>
            <input
              type="checkbox"
              checked={useColorSchemeBg}
              onChange={(e) => setUseColorSchemeBg(e.target.checked)}
              style={{ accentColor: '#E3425C', width: '16px', height: '16px', cursor: 'pointer' }}
            />
            <span style={{ color: 'rgba(255,255,255,0.7)', fontSize: '11px', fontWeight: 500 }}>
              Use color palette as background
            </span>
          </label>

          <div style={{ marginBottom: '16px' }}>
            <label style={{ color: 'rgba(255,255,255,0.6)', fontSize: '11px', fontWeight: 600, display: 'block', marginBottom: '8px', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Theme / Keyword</label>
            <input value={themeKeyword} onChange={e => setThemeKeyword(e.target.value)} placeholder="e.g., Barbie, Vintage..." style={{
              width: '100%', padding: '11px 14px', borderRadius: '10px', border: '1px solid rgba(255,255,255,0.12)',
              background: 'rgba(255,255,255,0.05)', color: 'white', fontSize: '14px', outline: 'none', boxSizing: 'border-box',
            }} />
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '4px', marginTop: '8px' }}>
              {[...suggestions, ...(['Gold & Black', 'Floral', 'Afro-chic', 'Pastel', 'Neon'].filter(s => !suggestions.includes(s)))].slice(0, 10).map(s => (
                <button key={s} onClick={() => setThemeKeyword(s)} style={{
                  padding: '4px 9px', borderRadius: '10px', fontSize: '10px', cursor: 'pointer',
                  border: '1px solid rgba(255,255,255,0.08)', background: themeKeyword === s ? 'rgba(227,66,92,0.15)' : 'transparent',
                  color: themeKeyword === s ? '#E3425C' : 'rgba(255,255,255,0.35)',
                }}>{s}</button>
              ))}
            </div>
          </div>

          {/* Mobile invitation heading */}
          <div style={{ marginBottom: '16px' }}>
            <label style={{ color: 'rgba(255,255,255,0.6)', fontSize: '11px', fontWeight: 600, display: 'block', marginBottom: '8px', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Invitation Heading</label>
            <select value={headingOption} onChange={e => setHeadingOption(e.target.value)} style={{
              width: '100%', padding: '11px 14px', borderRadius: '10px', border: '1px solid rgba(255,255,255,0.12)',
              background: 'rgba(255,255,255,0.05)', color: 'white', fontSize: '13px', outline: 'none', boxSizing: 'border-box',
              appearance: 'auto',
            }}>
              <option value="" style={{ background: '#1a1a2e' }}>You're Invited to (default)</option>
              <option value="You Are Cordially Invited to" style={{ background: '#1a1a2e' }}>You Are Cordially Invited to</option>
              <option value="You're Warmly Invited to" style={{ background: '#1a1a2e' }}>You're Warmly Invited to</option>
              <option value="We Request the Honour of Your Presence at" style={{ background: '#1a1a2e' }}>We Request the Honour of Your Presence at</option>
              <option value="Join Us in Celebrating" style={{ background: '#1a1a2e' }}>Join Us in Celebrating</option>
              <option value="Save the Date" style={{ background: '#1a1a2e' }}>Save the Date</option>
              <option value="In Loving Memory of" style={{ background: '#1a1a2e' }}>In Loving Memory of</option>
              <option value="Umemiwe" style={{ background: '#1a1a2e' }}>Umemiwe (Zulu)</option>
              <option value="O Memeletswe" style={{ background: '#1a1a2e' }}>O Memeletswe (Sotho/Tswana)</option>
              <option value="U Rambiwa" style={{ background: '#1a1a2e' }}>U Rambiwa (Venda)</option>
              <option value="custom" style={{ background: '#1a1a2e' }}>Custom...</option>
            </select>
            {(headingOption === 'custom' || (headingOption && !['', 'You Are Cordially Invited to', "You're Warmly Invited to", 'We Request the Honour of Your Presence at', 'Join Us in Celebrating', 'Save the Date', 'In Loving Memory of', 'Umemiwe', 'O Memeletswe', 'U Rambiwa'].includes(headingOption))) && (
              <input value={headingOption === 'custom' ? '' : headingOption} onChange={e => setHeadingOption(e.target.value)} placeholder="e.g., Woza Sizobungaza..." style={{
                width: '100%', padding: '10px 14px', borderRadius: '10px', border: '1px solid rgba(255,255,255,0.12)',
                background: 'rgba(255,255,255,0.05)', color: 'white', fontSize: '13px', outline: 'none', boxSizing: 'border-box', marginTop: '8px',
              }} />
            )}
          </div>

          {/* Mobile photo + inspiration uploads — compact row */}
          <div style={{ display: 'flex', gap: '8px', marginBottom: '16px' }}>
            <div style={{ flex: 1 }}>
              {!photoPreview ? (
                <button onClick={() => fileInputRef.current?.click()} style={{
                  width: '100%', padding: '10px', borderRadius: '10px', cursor: 'pointer',
                  border: '1px dashed rgba(255,255,255,0.2)', background: 'rgba(255,255,255,0.03)',
                  color: 'rgba(255,255,255,0.4)', fontSize: '11px', fontWeight: 500,
                  display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '6px',
                }}><FiCamera size={13} /> Photo</button>
              ) : (
                <div style={{ position: 'relative', borderRadius: '10px', overflow: 'hidden' }}>
                  <img src={photoPreview} alt="Photo" style={{ width: '100%', height: '60px', objectFit: 'cover', borderRadius: '10px' }} />
                  <button onClick={removePhoto} style={{ position: 'absolute', top: '3px', right: '3px', width: '20px', height: '20px', borderRadius: '50%', background: 'rgba(0,0,0,0.7)', border: 'none', color: 'white', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center' }}><FiX size={10} /></button>
                </div>
              )}
            </div>
            <div style={{ flex: 1 }}>
              {!inspirationPreview ? (
                <button onClick={() => inspoInputRef.current?.click()} style={{
                  width: '100%', padding: '10px', borderRadius: '10px', cursor: 'pointer',
                  border: '1px dashed rgba(255,255,255,0.2)', background: 'rgba(255,255,255,0.03)',
                  color: 'rgba(255,255,255,0.4)', fontSize: '11px', fontWeight: 500,
                  display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '6px',
                }}><FiImage size={13} /> Inspiration</button>
              ) : (
                <div style={{ position: 'relative', borderRadius: '10px', overflow: 'hidden' }}>
                  <img src={inspirationPreview} alt="Inspo" style={{ width: '100%', height: '60px', objectFit: 'cover', borderRadius: '10px' }} />
                  <button onClick={removeInspiration} style={{ position: 'absolute', top: '3px', right: '3px', width: '20px', height: '20px', borderRadius: '50%', background: 'rgba(0,0,0,0.7)', border: 'none', color: 'white', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center' }}><FiX size={10} /></button>
                </div>
              )}
            </div>
          </div>

          {/* Hidden file inputs */}
          <input ref={fileInputRef} type="file" accept="image/*" onChange={handlePhotoUpload} style={{ display: 'none' }} />
          <input ref={inspoInputRef} type="file" accept="image/*" onChange={handleInspirationUpload} style={{ display: 'none' }} />

          {/* Generate + Preview buttons — mobile */}
          <div style={{ display: 'flex', gap: '8px', marginBottom: '12px' }}>
            <button onClick={generateDesignPreview} disabled={loading || generatingDesignPreview} style={{
              flex: 1, padding: '14px', borderRadius: '12px', border: '1px solid rgba(255,255,255,0.2)',
              background: generatingDesignPreview ? 'rgba(255,255,255,0.05)' : 'transparent',
              color: generatingDesignPreview ? 'rgba(255,255,255,0.4)' : 'rgba(255,255,255,0.7)',
              fontSize: '14px', fontWeight: 600, cursor: (loading || generatingDesignPreview) ? 'not-allowed' : 'pointer',
              display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '6px',
            }}>
              {generatingDesignPreview ? <><FiRefreshCw size={14} style={{ animation: 'spin 1s linear infinite' }} /> Previewing...</> : <><FiEye size={14} /> Preview</>}
            </button>
            <button onClick={generateDesign} disabled={loading || generatingDesignPreview} style={{
              flex: 2, padding: '14px', borderRadius: '12px', border: 'none',
              background: loading ? 'rgba(227,66,92,0.3)' : 'linear-gradient(135deg, #E3425C, #D13A4F)',
              color: loading ? 'rgba(255,255,255,0.5)' : '#ffffff', fontSize: '15px', fontWeight: 700,
              cursor: (loading || generatingDesignPreview) ? 'not-allowed' : 'pointer',
              display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px',
            }}>
              {loading ? <><FiRefreshCw size={16} style={{ animation: 'spin 1s linear infinite' }} /> Generating...</> : <><FiZap size={16} /> Generate Design</>}
            </button>
          </div>

          {error && <div style={{ padding: '12px 14px', borderRadius: '10px', background: 'rgba(239,68,68,0.12)', border: '1px solid rgba(239,68,68,0.25)', color: '#FCA5A5', fontSize: '12px', marginBottom: '12px' }}>{error}</div>}

          {/* Premium upgrade on mobile — always below Generate button */}
          {imageUrl && !loading && <PremiumSection compact={true} />}

          {/* Empty state on mobile */}
          {!imageUrl && !loading && !generatingDesignPreview && !designPreviewUrl && (
            <div style={{ textAlign: 'center', padding: '30px 0', color: 'rgba(255,255,255,0.3)' }}>
              <div style={{ fontSize: '50px', marginBottom: '12px' }}>&#127912;</div>
              <p style={{ fontSize: '13px' }}>Choose a style and theme above, then tap Generate</p>
            </div>
          )}
        </div>
      </div>

      {/* Full Screen Viewer */}
      {fullscreen && imageUrl && (
        <div style={{
          position: 'fixed', inset: 0, zIndex: 99999,
          background: 'rgba(0,0,0,0.97)',
          display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
        }} onClick={() => setFullscreen(false)}>
          {/* Top bar */}
          <div style={{
            position: 'absolute', top: 0, left: 0, right: 0,
            padding: '10px 14px', display: 'flex', justifyContent: 'space-between', alignItems: 'center',
            background: 'linear-gradient(to bottom, rgba(0,0,0,0.8), transparent)',
            zIndex: 2,
          }}>
            <span style={{ color: 'rgba(255,255,255,0.6)', fontSize: '12px', fontWeight: 500 }}>AI Invitation</span>
            <div style={{ display: 'flex', gap: '6px', alignItems: 'center' }}>
              <button onClick={(e) => { e.stopPropagation(); applyAsInvitation(); }} style={{
                background: 'linear-gradient(135deg, #10B981, #059669)', border: 'none', borderRadius: '8px',
                padding: '8px 12px', color: 'white', fontSize: '11px', fontWeight: 600, cursor: 'pointer',
                display: 'flex', alignItems: 'center', gap: '4px',
              }}><FiCheck size={12} /> Use</button>
              <button onClick={(e) => { e.stopPropagation(); downloadImage(); }} style={{
                background: 'rgba(255,255,255,0.15)', border: 'none', borderRadius: '8px',
                padding: '8px 12px', color: 'white', fontSize: '11px', fontWeight: 500, cursor: 'pointer',
                display: 'flex', alignItems: 'center', gap: '4px',
              }}><FiDownload size={12} /> Save</button>
              <button onClick={() => setFullscreen(false)} style={{
                background: 'rgba(255,255,255,0.15)', border: 'none', borderRadius: '50%',
                width: '30px', height: '30px', color: 'white', fontSize: '16px',
                cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center',
              }}>&times;</button>
            </div>
          </div>

          <img src={imageUrl} alt="Full Screen" onClick={(e) => e.stopPropagation()} style={{
            maxWidth: '95vw', maxHeight: '88vh', objectFit: 'contain', borderRadius: '8px',
          }} />

          <div style={{ position: 'absolute', bottom: '10px', color: 'rgba(255,255,255,0.2)', fontSize: '10px' }}>
            Tap anywhere to close — Long press to save
          </div>
        </div>
      )}

      <style>{`
        @keyframes spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }
        @keyframes pulse { 0%, 80%, 100% { opacity: 0.3; transform: scale(0.8); } 40% { opacity: 1; transform: scale(1.2); } }

        /* Desktop: show desktop layout, hide mobile */
        .ai-designer-desktop { display: flex !important; }
        .ai-designer-mobile { display: none !important; }

        @media (max-width: 768px) {
          /* Mobile: hide desktop layout, show mobile */
          .ai-designer-desktop { display: none !important; }
          .ai-designer-mobile { display: flex !important; }
        }
      `}</style>
    </div>
  );
}
